import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  PlusIcon,
  Typography
} from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { UserSummary } from "@dealer-center/ddc-pages-configuration-interface";
import {
  BORDER_MUTED,
  SPACE_BLOCK,
  SPACE_SECTION,
  SURFACE_DEFAULT,
  SURFACE_MUTED,
  belowMobile,
  mobileOnly
} from "../../../components/UserInfo";
import type { UserListSortOption } from "../../useUserListQuery";
import { ListPager } from "./ListPager";
import { ListToolbar } from "./ListToolbar";
import { ResultCount } from "./ResultCount";
import { UserRow } from "./UserRow";

/**
 * **320px, overriding the prototype's `_Aside Left Rail = 240px fixed`** — as the prototype itself
 * does in its own markup. Three values per row need the width; 240px wraps every email.
 */
const PANEL_WIDTH = "320px";

/**
 * The rows box, and the reason it needs this much CSS.
 *
 * The prototype's `.um-rows` is a **white, 1px-outlined, rounded box with a single divider between
 * rows** — the rail's one card-like surface, sitting on the page background. Interstate's `List`
 * instead draws a **full 1px border around every item**, collapsed with `margin-bottom: -1px`, which
 * on its own reads as a stack of boxes rather than one list and doubles up against a container
 * border at the top and bottom edges.
 *
 * So the border moves to the container and the items keep only their horizontal rules:
 *
 * - left/right go **`transparent`, not `none`** — `none` would remove 1px from the box model and
 *   knock every unselected row 1px out of line with the selected one, whose padding Interstate
 *   already reduces to compensate for its 4px selection marker;
 * - top **and** bottom stay coloured, because the `-1px` collapse overlaps them and one of the two
 *   has to paint the divider;
 * - the first row's top and the last row's bottom go transparent, so the container's own outline is
 *   the only line at the edges.
 *
 * `ids-list-item-content` / `ids-list-group-item` are Interstate's **own semantic class names**, not
 * generated hashes, so this is the same kind of reach-past-the-API that `IdentityHeader` documents
 * for `.MuiAvatar-root` — stable across a rebuild, but a place an Interstate upgrade could regress.
 */
const ROWS_SURFACE = {
  backgroundColor: SURFACE_DEFAULT,
  border: `1px solid ${BORDER_MUTED}`,
  borderRadius: "4px",
  flex: "1 1 auto",
  /*
   * FR-005's first scroll region — **at desktop only.** Below `md` the rows grow and the page
   * scrolls once: nested touch scrolling in a ~600px-high viewport is the trap TA4236901 names, and
   * `minHeight: 0` here is precisely what would pin the rows into a short inner scroller.
   */
  minHeight: 0,
  /*
   * ⚠️ **Both axes have to relax together, and `overflowX` is the one that is easy to miss.**
   * CSS resolves `overflow-x: hidden` + `overflow-y: visible` by computing the `visible` side as
   * `auto` — so leaving `overflowX: "hidden"` in place below `md` would quietly reinstate the inner
   * scroller this is trying to remove, and the page would have two scrollbars on a phone. The
   * `visible` value is only honoured when *neither* axis is clipped.
   */
  overflowX: "hidden",
  overflowY: "auto",
  ...belowMobile({
    minHeight: "auto",
    overflowX: "visible",
    overflowY: "visible"
  }),
  "& .ids-list-item-content": {
    borderBottomColor: SURFACE_MUTED,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderTopColor: SURFACE_MUTED
  },
  "& .ids-list-group-item:first-of-type .ids-list-item-content": {
    borderTopColor: "transparent"
  },
  "& .ids-list-group-item:last-child .ids-list-item-content": {
    borderBottomColor: "transparent"
  }
} as const;

export interface UserListPanelProps {
  users: UserSummary[];
  totalCount: number;
  totalPages: number;
  page: number;
  isLoading: boolean;
  isError: boolean;
  isFiltered: boolean;
  filterInput: string;
  /**
   * The **settled** filter term, quoted back by FR-015's no-matches message. Distinct from
   * `filterInput` on purpose — see `useUserListQuery`'s `filterTerm`.
   */
  filterTerm: string;
  sortOption: UserListSortOption;
  selectedUserId?: string;
  /**
   * Whether the mobile drill-down is currently showing the **detail** pane, so this one is hidden.
   *
   * Only consulted below the breakpoint — at desktop both panes are always visible, so the panel
   * ignores it. Passed in rather than read from `useDrillDownView` here, because this component is
   * otherwise free of routing and keeping it that way is what lets `UserListPanel.spec.tsx` render
   * it without a router.
   */
  isHiddenOnMobile?: boolean;
  onFilterChange: (term: string) => void;
  onSortChange: (option: UserListSortOption) => void;
  onPageChange: (page: number) => void;
  onSelectUser: (user: UserSummary) => void;
  onAddUser: () => void;
}

/**
 * The user list — **FR-005's first scroll region**.
 *
 * The layout contract: the toolbar and result-count line are **fixed at the top**, the pager is
 * **fixed at the foot**, and **only the rows scroll** between them. That is why this component
 * establishes a flex column with `minHeight: 0` and gives `flex: 1 1 auto; overflow-y: auto` to the
 * rows box alone — the two fixed clusters carry `flexShrink: 0`. The chain matters: a single missing
 * `minHeight: 0` anywhere above lets the rows box grow instead of scroll, and the whole page scrolls
 * as one block, which is exactly the defect FR-005 exists to prevent. **jsdom cannot detect that
 * regression** (it computes no layout), which is why T097 covers it in Cypress.
 *
 * ## The rail is NOT part of the detail card — corrected 2026-09-15
 *
 * `contracts/user-list-panel.md` quotes the design intent as *"List Detail: left rail = List (**no
 * card**). Toolbar, then independently-scrolling List, then pager"*, and the prototype builds exactly
 * that: `.pl-aside-left` is a transparent 320px column sitting on the page background, with
 * `.um-list{background:transparent;border:none}` inside it. **The one bordered surface in the rail is
 * the rows box** (`.um-rows`); the detail panel is a separate card, inset by `.pl-main`'s 16px
 * gutter.
 *
 * It shipped as the opposite: rail and detail panel inside **one** white bordered card, divided by a
 * `border-right`, with 24px of padding around the rail. That reads as a single split surface rather
 * than a list beside a card, and it is why the rail did not look like the approved design however
 * right its contents were. The rail therefore now carries **no border, no padding and no background
 * of its own** — see `ROWS_SURFACE` for where the outline went, and `UserManager` for the card.
 *
 * Band spacing is 16 / 8 / 16 and **each band owns its own gap** (`ListToolbar`'s `paddingBottom`,
 * `ResultCount`'s `paddingBottom`, `ListPager`'s `paddingTop`) rather than coming from one `rowGap`
 * here, because the prototype's three gaps are not equal.
 *
 * **FR-015's three states are distinguished, not collapsed:**
 *
 * | State | Condition | Why it is its own message |
 * |---|---|---|
 * | no users on the account | `totalCount === 0`, no filter | a fact about the account |
 * | nothing matched the filter | `totalCount === 0`, filter applied | a fact about the *query* |
 * | the read failed | `isError` | a fact about **nothing** — we do not know what exists |
 *
 * Both empty states arrive as `totalCount: 0`, so the discriminator is the applied filter term,
 * which only the UI holds. The third is **not** an empty payload at all. Story 1 scenario 6 forbids
 * the collapse in as many words: **an empty list MUST NOT be presented as if the account had no
 * users.**
 *
 * **Loading precedes empty**, for the reason `TabState` gives for the tabs: an empty state rendered
 * while the query is in flight reads as "this account has no users".
 *
 * **Selection uses Interstate's own mechanism** — `List` with `selectable` and a controlled
 * `selected` id — rather than a hand-rolled one, so the visual state is the design system's.
 * `UserRow` adds the *programmatic* half, because `ListItem` exposes no aria props at all (FR-016).
 */
export const UserListPanel = ({
  users,
  totalCount,
  totalPages,
  page,
  isLoading,
  isError,
  isFiltered,
  filterInput,
  filterTerm,
  sortOption,
  selectedUserId,
  isHiddenOnMobile = false,
  onFilterChange,
  onSortChange,
  onPageChange,
  onSelectUser,
  onAddUser
}: UserListPanelProps) => (
  <Box
    data-testid={"user-list-panel"}
    sx={{
      display: "flex",
      /*
       * `.pl-aside-left{width:320px;flex:0 0 320px}` — a fixed rail, never a shrinking one.
       *
       * Below `md` it is not a rail at all: it is the whole screen, so the fixed basis and width
       * both give way to `auto`/`100%`. A 320px `flex-basis` in a column flex container would set
       * the *height*, which is why the basis has to change and not just the width.
       */
      flex: `0 0 ${PANEL_WIDTH}`,
      flexDirection: "column",
      minHeight: 0,
      width: PANEL_WIDTH,
      ...belowMobile({
        /*
         * `isHiddenOnMobile` only ever hides the panel **inside** this media query — above the
         * boundary both panes are always visible, so the base keeps `display: flex`.
         */
        display: isHiddenOnMobile ? "none" : "flex",
        flex: "1 1 auto",
        minHeight: "auto",
        width: "100%"
      })
    }}
  >
    {/*
     * The mobile list header (TA4236901): the heading and the add-user action.
     *
     * Mobile-only because at desktop the page `h1` is right above this and a second "Users" heading
     * would be redundant. Below the breakpoint `PageHeading` compacts and this becomes the list's
     * own title, which is what the mockup shows.
     */}
    <Box
      data-testid={"user-list-mobile-header"}
      sx={{
        alignItems: "center",
        columnGap: SPACE_BLOCK,
        justifyContent: "space-between",
        ...mobileOnly("flex")
      }}
    >
      <Typography tag={"h2"} variant={"h3"}>
        {Locale.getText("USERS") as string}
      </Typography>

      {/*
       * ## Icon-only, and that is an i18n constraint rather than a style choice
       *
       * A visible "+ User" label is not available: there is no registered `USER` key, and the
       * nearest registered label is `ADD_USER` — "Add User", but **"Ajouter un utilisateur"** in
       * fr-CA, 22 characters that will not sit beside "Utilisateurs" in a 390px header. Composing
       * `ADD` with a bare noun would invent a string and break in French word order. So the label
       * becomes the *accessible name* and the visible width is a fixed 48px in every locale.
       *
       * A header button, not a FAB or sticky bar: a full-width bar above the pager would crowd it.
       */}
      <Button
        analyticsConfiguration={{
          eventName: "usermanager-add-user-mobile",
          eventProperties: undefined
        }}
        aria-label={Locale.getText("ADD_USER") as string}
        buttonStyle={"tertiary"}
        data-testid={"user-list-mobile-add-user"}
        icon={<PlusIcon title={""} />}
        size={"lg"}
        onClick={onAddUser}
      />
    </Box>

    <ListToolbar
      filterInput={filterInput}
      sortOption={sortOption}
      onAddUser={onAddUser}
      onFilterChange={onFilterChange}
      onSortChange={onSortChange}
    />

    {/*
     * The count line is fixed with the toolbar rather than scrolling with the rows: it is a
     * statement about the whole result set, so it must stay visible while the rows move.
     * Suppressed while loading and on failure — a count of 0 during either would be a claim we
     * cannot support.
     */}
    {!isLoading && !isError && (
      <ResultCount
        isFiltered={isFiltered}
        pageCount={users.length}
        totalCount={totalCount}
      />
    )}

    {/*
     * THE ONLY SCROLLER in this panel. `flex: 1 1 auto` + `minHeight: 0` + `overflow-y: auto` is
     * the whole mechanism; removing any one of the three breaks FR-005.
     *
     * **`role="group"` + `aria-label` live HERE, not on `List`, because `ListProps` accepts no aria
     * props at all** — verified against the type, and the reason is worth stating: an `aria-label`
     * passed to `List` is **silently dropped**, so the rail rendered as an unnamed list and
     * `UM_USER_LIST_ARIA_LABEL` was dead copy. Nothing surfaced it; `getByLabelText` in
     * `UserListPanel.spec.tsx` is what caught it (2026-09-14).
     *
     * This is the same constraint that forces `UserRow`'s visually-hidden selection marker —
     * `ListItem` exposes no aria props either — so the workaround is the sanctioned one for this
     * component family rather than a shortcut. `role="group"` is the honest role for a set of
     * related controls and takes a name from `aria-label`; a bare labelled `Box` would not be
     * exposed at all. **Do not "tidy" the label back onto `List`.**
     */}
    <Box
      aria-label={Locale.getText("UM_USER_LIST_ARIA_LABEL") as string}
      data-testid={"user-list-rows"}
      role={"group"}
      sx={ROWS_SURFACE}
    >
      {isLoading ? (
        <Box
          data-testid={"user-list-loading"}
          sx={{
            alignItems: "center",
            display: "flex",
            height: "100%",
            justifyContent: "center"
          }}
        >
          <LoadingIndicator
            actionName={"usermanager-user-list"}
            text={Locale.getText("analytics.LOADING") as string}
          />
        </Box>
      ) : isError ? (
        /*
         * Inset, like the prototype's, because the rows box is now a bordered surface rather than a
         * bare region — an alert or a message flush against that outline reads as a rendering bug.
         */
        <Box sx={{ padding: SPACE_BLOCK }}>
          <Alert data-testid={"user-list-failed"} role={"alert"} type={"error"}>
            {Locale.getText("UM_LIST_FAILED") as string}
          </Alert>
        </Box>
      ) : totalCount === 0 ? (
        <Typography
          data-testid={
            isFiltered
              ? "user-list-empty-no-matches"
              : "user-list-empty-no-users"
          }
          /* `.um-empty{padding:var(--ids-sem-space-4) var(--ids-sem-space-3);text-align:center}` */
          sx={{
            padding: `${SPACE_SECTION} ${SPACE_BLOCK}`,
            textAlign: "center"
          }}
          tag={"p"}
          variant={"body-md"}
        >
          {/*
           * The no-matches message **quotes the term back**, per the approved design's
           * `No users match “{0}”.` — so the operator can see *what* was searched for rather than
           * being told only that it failed. It reads the **settled** term (`filterTerm`), not
           * `filterInput`, so the quoted text and the rows always describe the same query.
           */}
          {isFiltered
            ? String(
                Locale.getText("UM_EMPTY_NO_MATCHES", { term: filterTerm })
              )
            : (Locale.getText("UM_EMPTY_NO_USERS") as string)}
        </Typography>
      ) : (
        /* No `aria-label` here — `ListProps` has none. The name is on the wrapper above. */
        <List selectable data-testid={"user-list"} selected={selectedUserId}>
          {users.map(user => (
            <ListItem
              data-testid={`user-list-item-${user.userId}`}
              id={user.userId}
              key={user.userId}
              onClick={() => onSelectUser(user)}
            >
              {/*
               * `onSelect` goes to the **row**, which is the focusable, keyboard-operable element —
               * `ListItem` renders a plain `<div>` with no `role` or `tabIndex`, so its own
               * `onClick` above serves the mouse only. See `UserRow`'s comment.
               */}
              <UserRow
                isSelected={user.userId === selectedUserId}
                user={user}
                onSelect={() => onSelectUser(user)}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>

    <ListPager
      page={page}
      totalPages={totalPages}
      onPageChange={onPageChange}
    />
  </Box>
);
